import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "@/pages/about/components/ContactForm";
import { site } from "@/mocks/site";

const principles = [
  {
    icon: "ri-focus-2-line",
    title: "只呈现真实被讨论的",
    desc: "进榜的前提是话题确实在被广泛围观。我们不制造议题，只做整理与呈现，让榜单反映真实的讨论热度。",
  },
  {
    icon: "ri-scales-3-line",
    title: "标注信息，不代替判断",
    desc: "热度、涨跌、讨论量都尽量标注清楚。我们提供的是围观入口，结论留给你自己在看完内容之后下。",
  },
  {
    icon: "ri-refresh-line",
    title: "保持更新与可纠正",
    desc: "热度会退、名次会变。收到反馈后我们会重新核实相关内容，确认有误就及时修正或调整展示。",
  },
  {
    icon: "ri-heart-3-line",
    title: "理性围观，友善讨论",
    desc: "我们鼓励先看清楚再发言。评论区更希望看到有信息量的补充，而不是单纯的情绪输出。",
  },
];

const milestones = [
  { value: "6", label: "内容频道", desc: "覆盖娱乐、影视、网红、社会与生活方向" },
  { value: "128", label: "在榜话题", desc: "按热度动态进出榜，不做永久收录" },
  { value: "15", label: "分钟级更新", desc: "顶部话题滚动刷新，实时标记更醒目" },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: `关于我们 - ${site.name}`,
      url: "https://51.cg/about",
      description: site.intro,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.name,
      url: "https://51.cg/",
      email: site.email,
      description: site.intro,
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title="关于我们 - 51吃瓜榜 品牌介绍与内容原则"
        description="了解51吃瓜榜的定位、内容原则与团队理念。我们以热度排行为核心，整理正在被广泛讨论的焦点事件，帮助围观网友快速看清今日热议话题。"
        keywords="51吃瓜榜,关于51吃瓜榜,热门吃瓜榜,内容原则,焦点事件排行"
        path="/about"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center lg:gap-12">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-label font-medium text-primary-700">
                  <i className="ri-fire-line"></i>
                  关于 {site.name}
                </span>
                <h1 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
                  把今天的热闹，整理成一份看得懂的榜单
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {site.intro}
                  我们希望它更像一份“围观地图”：不堆信息，而是按热度排好序，让你用最少的时间，找到最值得看一眼的那几条。
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/#rank"
                    className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-fire-fill"></i>
                    去看今日榜单
                  </Link>
                  <a
                    href="#contact"
                    className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-label font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-mail-line"></i>
                    联系我们
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-72">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20scene%20with%20open%20notebook%20showing%20a%20hand%20drawn%20ranking%20chart%2C%20coffee%20cup%20and%20warm%20desk%20lamp%2C%20soft%20amber%20light%2C%20cozy%20workspace%2C%20no%20people%2C%20clean%20minimal%20photography%2C%20terracotta%20and%20cream%20palette&width=1200&height=900&seq=cg-about-hero&orientation=landscape"
                    alt="51吃瓜榜编辑整理榜单的工作台场景"
                    title="51吃瓜榜品牌介绍 - 内容整理与热度排行"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-5">
                <SectionHeading
                  eyebrow="网站定位"
                  title="一个专注“看清热点”的聚合门户"
                  desc="信息从来不缺，缺的是排序和整理。51吃瓜榜 把散落在各个平台的讨论汇成一份榜单，把注意力还给真正值得看的内容。"
                />
                <dl className="mt-8 flex flex-col gap-4">
                  {milestones.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-5 rounded-lg border border-background-200 bg-background-100 p-5"
                    >
                      <span className="font-heading text-3xl font-black text-primary-600">
                        {item.value}
                      </span>
                      <span className="flex flex-col">
                        <span className="font-label text-sm font-semibold text-foreground-900">
                          {item.label}
                        </span>
                        <span className="mt-0.5 text-xs leading-relaxed text-foreground-500">
                          {item.desc}
                        </span>
                      </span>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="lg:col-span-7">
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  我们的内容原则
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  这四条是 51吃瓜榜 处理每一条内容时的基本判断标准，也是我们愿意长期坚持下去的事。
                </p>
                <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {principles.map((item) => (
                    <article
                      key={item.title}
                      className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5"
                    >
                      <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className={`${item.icon} text-xl`}></i>
                      </span>
                      <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                        {item.desc}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
            <SectionHeading
              eyebrow="用户口碑"
              title="常来围观的人这样评价"
              desc="我们收集了部分用户的真实反馈，它们也在提醒我们：榜单的价值，在于让人少绕弯。"
              align="center"
            />
            <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-5 md:grid-cols-3">
              {[
                { user: "夜读型网友", text: "睡前刷五分钟就能跟上进度，比我一个个点开热搜快多了。" },
                { user: "影视区常驻", text: "影视综艺频道是我每周必看的，能看到别人怎么评价结局和选角。" },
                { user: "内容编辑小林", text: "做选题前会先扫一眼涨热榜，判断话题还在不在爬升期很实用。" },
              ].map((item) => (
                <figure
                  key={item.user}
                  className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-double-quotes-l text-lg"></i>
                  </span>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                    {item.text}
                  </blockquote>
                  <figcaption className="mt-4 text-xs font-label text-foreground-500">
                    —— {item.user}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full bg-background-50">
          <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-5">
                <SectionHeading
                  eyebrow="联系我们"
                  title="有线索、有意见，都欢迎直接说"
                  desc="我们更愿意把反馈渠道做得简单一点。填写左侧表单，或者直接发邮件，都能找到我们。"
                />
                <ul className="mt-8 flex flex-col gap-4">
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="font-label text-sm font-semibold text-foreground-900">
                        邮箱联系
                      </span>
                      <span className="mt-0.5 text-sm text-foreground-600">{site.email}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className="ri-time-line text-lg"></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="font-label text-sm font-semibold text-foreground-900">
                        处理时效
                      </span>
                      <span className="mt-0.5 text-sm text-foreground-600">{site.replyTime}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-shield-check-line text-lg"></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="font-label text-sm font-semibold text-foreground-900">
                        信息使用
                      </span>
                      <span className="mt-0.5 text-sm text-foreground-600">
                        仅用于处理你的反馈，不做其他用途。
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}